import { css } from '@emotion/react';

import { COLORS2 } from '@/Client/Styles';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { MODAL_COLORS } from '@/Client/Widgets/Modal/ModalStyleLib';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

// Shared scan-video presentation primitives — modal head/foot chrome, file-list rows,
// and ACCENT/DARK_BG tokens for VideoToolbox, ScanVideoMedia, and the upload modal.

export const ACCENT = COLORS2.alwaysDark.yellow;
export const DARK_BG = COLORS2.alwaysDark.charcoal;

// ********************************************************************
// Modal head / foot chrome
// ********************************************************************

export const EYEBROW_STYLE = css`
  label: SCAN_VIDEO_EYEBROW;
  align-self: flex-start;
  display: inline-block;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${styleguideColors.ink};
  background: ${styleguideColors.white};
  padding: 5px 9px;
`;

export const HEAD_STYLE = css`
  label: SCAN_VIDEO_HEAD;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TITLE_STYLE = css`
  label: SCAN_VIDEO_TITLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(22)};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${MODAL_COLORS.text};
`;

export const SUBTITLE_STYLE = css`
  label: SCAN_VIDEO_SUBTITLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  color: ${MODAL_COLORS.textMuted};
`;

export const FOOT_WITH_HINT_STYLE = css`
  label: SCAN_VIDEO_FOOT_WITH_HINT;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 16px;
`;

export const FOOT_ACTIONS_STYLE = css`
  label: SCAN_VIDEO_FOOT_ACTIONS;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  margin-left: auto;
`;

// ********************************************************************
// Video / file list rows + thumbnails
// ********************************************************************

export const SCAN_VIDEO_FILE_LIST_MAX_VISIBLE_ROWS = 4;

/** Row body (52px min) + optional 3px progress bar — use the taller slot so height math stays stable. */
const FILE_LIST_ROW_BODY_PX = 52;
const FILE_LIST_ROW_BAR_PX = 3;
const FILE_LIST_ROW_SLOT_PX = FILE_LIST_ROW_BODY_PX + FILE_LIST_ROW_BAR_PX;
const FILE_LIST_ROW_GAP_PX = 8;

export const getFileListMaxHeightPx = (
  visibleRows = SCAN_VIDEO_FILE_LIST_MAX_VISIBLE_ROWS,
) => visibleRows * FILE_LIST_ROW_SLOT_PX + (visibleRows - 1) * FILE_LIST_ROW_GAP_PX;

export const FILE_LIST_STYLE = css`
  label: SCAN_VIDEO_FILE_LIST;
  display: flex;
  flex-direction: column;
  gap: ${FILE_LIST_ROW_GAP_PX}px;
  margin-top: 16px;
  min-height: 0;
  overscroll-behavior: contain;
`;

/** Cap list height and scroll once file count exceeds the visible row budget. */
export const fileListScrollStyle = (fileCount: number) => {
  const maxHeightPx = getFileListMaxHeightPx();
  const scrollable = fileCount > SCAN_VIDEO_FILE_LIST_MAX_VISIBLE_ROWS;
  return css`
    max-height: ${maxHeightPx}px;
    overflow-y: auto;
    ${scrollable ? `height: ${maxHeightPx}px;` : ''}
  `;
};

export const fileRowStyle = (isBad: boolean) =>
  css`
  label: SCAN_VIDEO_FILE_ROW_BATCH;
  flex: 0 0 auto;
  border: 1px solid ${isBad ? COLORS2.alwaysDark.red : MODAL_COLORS.text};
  background: ${isBad ? MODAL_COLORS.dangerBackground : styleguideColors.surfaceMuted};
  overflow: hidden;
`;

export const fileThumbStyle = (isBad: boolean) =>
  css`
  label: SCAN_VIDEO_FILE_THUMB_BATCH;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${isBad ? COLORS2.alwaysDark.red : DARK_BG};
  color: ${isBad ? COLORS2.alwaysDark.white : ACCENT};
`;

export const fileSubStyle = (isBad: boolean) =>
  css`
  label: SCAN_VIDEO_FILE_SUB_BATCH;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  line-height: 1.2;
  letter-spacing: 0.06em;
  color: ${isBad ? COLORS2.alwaysDark.red : MODAL_COLORS.textMuted};
  text-transform: uppercase;
`;

export const FILE_ROW_TOP_STYLE = css`
  label: SCAN_VIDEO_FILE_ROW_TOP;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 52px;
  padding: 0 16px;
`;

export const FILE_INFO_STYLE = css`
  label: SCAN_VIDEO_FILE_INFO;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

export const FILE_NAME_STYLE = css`
  label: SCAN_VIDEO_FILE_NAME;
  font-family: ${FONT.body};
  font-size: ${pxToRem(14)};
  font-weight: 700;
  line-height: 1.2;
  color: ${MODAL_COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
